import { useState, useEffect } from "react";
import { ArrowDown, Loader2, X } from "lucide-react";
import {
  RetroDialog,
  RetroDialogHeader,
  RetroDialogBody,
  RetroDialogFooter,
} from "@/components/ui/retro-dialog";
import { usePlaylists, type PlaylistItem } from "@/hooks/usePlaylists";
import { isPWA } from "@/lib/platform";
import { requestInstallPrompt } from "@/lib/installPromptBus";
import {
  downloadEpisode,
  isEpisodeOffline,
  OfflineDownloadAbortError,
} from "@/lib/offlineEpisodes";
import { toast } from "sonner";

interface Props {
  playlistTitle: string;
  items?: PlaylistItem[];
  playlistId?: string;
}

// Same comic-button skeleton used by OfflineEpisodeButton so the styling stays
// 1:1 with the per-episode download badge on the saved-episodes page.
const comicBase =
  "comic-play-btn relative overflow-hidden flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-2 sm:px-3 py-1 border-[2px] rounded-md transition-all duration-150 transform hover:scale-[1.03] hover:rotate-[-0.5deg] active:scale-95";
const shadowState =
  "shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px]";
const teal =
  "bg-[hsl(195,85%,90%)] text-[hsl(195,80%,20%)] border-[hsl(195,80%,28%)]";

export default function PlaylistDownloadButton({
  playlistTitle,
  items: providedItems,
  playlistId,
}: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [abort, setAbort] = useState<AbortController | null>(null);
  const [resolvedItems, setResolvedItems] = useState<PlaylistItem[] | undefined>(
    providedItems,
  );
  const [resolving, setResolving] = useState(false);

  const { fetchPlaylistItems } = usePlaylists();

  useEffect(() => {
    if (providedItems !== undefined) setResolvedItems(providedItems);
  }, [providedItems]);

  const ensureItems = async (): Promise<PlaylistItem[]> => {
    if (resolvedItems) return resolvedItems;
    if (!playlistId) return [];
    setResolving(true);
    try {
      const fetched = await fetchPlaylistItems(playlistId);
      setResolvedItems(fetched);
      return fetched;
    } finally {
      setResolving(false);
    }
  };

  const downloadable = resolvedItems?.filter((i) => !!i.episode?.audio_url) ?? [];
  const total = downloadable.length;

  if (!playlistId && providedItems === undefined) return null;
  if (providedItems !== undefined && total === 0) return null;


  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isPWA()) {
      requestInstallPrompt("feature-blocked", { force: true });
      return;
    }
    const fetched = await ensureItems();
    const count = fetched.filter((i) => !!i.episode?.audio_url).length;
    if (count === 0) {
      toast.message("No downloadable episodes in this playlist.");
      return;
    }
    setConfirmOpen(true);
  };

  const handleCancel = () => {
    abort?.abort();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    const itemsToDownload =
      resolvedItems?.filter((i) => !!i.episode?.audio_url) ?? [];
    const controller = new AbortController();
    setAbort(controller);
    setDownloading(true);
    setCompleted(0);
    let okCount = 0;
    let skipCount = 0;
    let failCount = 0;
    try {
      for (let i = 0; i < itemsToDownload.length; i++) {
        if (controller.signal.aborted) break;
        const item = itemsToDownload[i];
        const ep = item.episode!;
        try {
          const already = await isEpisodeOffline(ep.id);
          if (already) {
            skipCount++;
          } else {
            await downloadEpisode(
              {
                episodeId: ep.id,
                audioUrl: ep.audio_url!,
                title: ep.title,
                podcastName: ep.podcast?.name || "",
                artworkUrl: ep.podcast?.artwork_url || undefined,
                durationSeconds: ep.duration_seconds || undefined,
              },
              undefined,
              controller.signal,
            );
            okCount++;
          }
        } catch (err) {
          if (err instanceof OfflineDownloadAbortError) break;
          console.error("Playlist download failed for episode", ep.id, err);
          failCount++;
        }
        setCompleted(i + 1);
      }

      if (controller.signal.aborted) {
        toast.message(`Playlist download canceled (${okCount} saved)`);
      } else if (failCount === 0) {
        toast.success(
          okCount > 0
            ? `Saved ${okCount} episode${okCount === 1 ? "" : "s"} for offline${skipCount ? ` (${skipCount} already saved)` : ""}`
            : "All episodes already saved offline",
        );
      } else {
        toast.error(
          `Saved ${okCount}, ${failCount} failed${skipCount ? `, ${skipCount} already saved` : ""}`,
        );
      }
    } finally {
      setDownloading(false);
      setAbort(null);
      setCompleted(0);
    }
  };

  if (downloading) {
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return (
      <div className="inline-flex items-center gap-1">
        <div
          className={`${comicBase} ${shadowState} ${teal}`}
          aria-label={`Downloading playlist ${completed}/${total}`}
          title={`Downloading playlist — ${completed}/${total}`}
        >
          <Loader2 className="w-3 h-3 animate-spin relative z-10" strokeWidth={2.5} />
          <span className="relative z-10">
            {completed}/{total}
          </span>
          <span
            className="absolute bottom-0 left-0 h-1 bg-[hsl(195,80%,28%)]/30 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <button
          type="button"
          onClick={handleCancel}
          title="Cancel playlist download"
          aria-label="Cancel playlist download"
          className={`${comicBase} ${shadowState} bg-[hsl(var(--destructive)/10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]`}
        >
          <X className="w-3 h-3 relative z-10" strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  if (resolving) {
    return (
      <div
        className={`${comicBase} ${shadowState} ${teal} opacity-80 cursor-wait`}
        aria-label="Loading playlist episodes"
        title="Loading playlist episodes"
      >
        <Loader2 className="w-3 h-3 animate-spin relative z-10" strokeWidth={2.5} />
        <span className="relative z-10">Loading…</span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        title={
          isPWA()
            ? total > 0
              ? `Download all ${total} episode${total === 1 ? "" : "s"} in this playlist for offline listening.`
              : "Download all episodes in this playlist for offline listening."
            : "Install AskThePods to download playlists for offline listening."
        }
        className={`${comicBase} ${shadowState} ${teal}`}
      >
        <ArrowDown className="w-3 h-3 relative z-10" strokeWidth={2.5} />
        <span className="relative z-10">Download all</span>
      </button>

      <RetroDialog open={confirmOpen} onOpenChange={setConfirmOpen} size="sm">
        <RetroDialogHeader
          title="Download this playlist?"
          description={`Confirm you want to download all ${total} episode${total === 1 ? "" : "s"} in “${playlistTitle}” to be available offline.`}
        />
        <RetroDialogBody>
          <p className="font-mono text-xs text-muted-foreground">
            Episodes already saved will be skipped. You can cancel anytime while downloading.
          </p>
        </RetroDialogBody>
        <RetroDialogFooter>
          <button onClick={() => setConfirmOpen(false)} className="btn-comic-ghost">
            Cancel
          </button>
          <button onClick={handleConfirm} className="btn-comic-primary">
            <ArrowDown className="w-3.5 h-3.5" strokeWidth={2.5} />
            Download all
          </button>
        </RetroDialogFooter>
      </RetroDialog>
    </>
  );
}
